import { useEffect, useMemo, useState, type ReactNode } from "react";
import { ApiError, formatBirth, type ReadingResponse, type UserView } from "../api";
import { IconStar } from "../icons";
import { firstName } from "../platform";
import { KinDiagram, StarDiagram, Trio } from "../StarDiagram";
import { AiReportButton, ExpandButton, LoadError, LockedList, Notes, Overlay, PdfButton, RichText, SectionBlock, Skeleton, firstBullet, type DocSection } from "../ui";
import { api } from "../api";

export type Loader = () => Promise<ReadingResponse>;

interface Props {
  title: string;
  moduleId: string;
  load: Loader;
  user: UserView;
  /** null — разбор нельзя добавить в избранное (пары, прогнозы, расклады). */
  favorite: boolean | null;
  onFavorite: () => void;
  onClose: () => void;
  onPaywall: () => void;
  onLoaded?: (r: ReadingResponse) => void;
  /** Владелец включил кнопку «Получить разбор полностью» — иначе она не показывается вовсе. */
  aiExpandEnabled: boolean;
  aiReportEnabled: boolean;
}

/** Результат расчёта: тёмный экран с диаграммой/числами и белый лист с разделами. */
function pdfOrThrow(r: { url: string } | { status: string }): { url: string } {
  if ("url" in r) return r;
  throw new Error("pdf_not_ready");
}

export function ResultScreen({ title, moduleId, load, user, favorite, onFavorite, onClose, onPaywall, onLoaded, aiExpandEnabled, aiReportEnabled }: Props) {
  const [data, setData] = useState<ReadingResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let alive = true;
    setError(null);
    load().then(
      (r) => {
        if (!alive) return;
        setData(r);
        onLoaded?.(r);
      },
      (e) => alive && setError(e instanceof ApiError && e.status === 400 ? "Не получилось разобрать дату. Проверьте день, месяц и год." : "Не удалось построить разбор. Попробуйте ещё раз."),
    );
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attempt]);

  const doc = data?.reading.doc;
  const canDownload = doc && doc.locked.length === 0 && !NEEDS_OTHER_DATE.has(moduleId);

  return (
    <Overlay
      title={moduleId === "star" ? undefined : title}
      onClose={onClose}
      back
      action={
        <>
          {canDownload && <PdfButton load={() => api.pdfReadingLink(moduleId)} compact />}
          {favorite !== null && (
            <button className={`favbtn${favorite ? " on" : ""}`} onClick={onFavorite} aria-label={favorite ? "Убрать из избранного" : "Добавить в избранное"} aria-pressed={favorite}>
              <IconStar />
            </button>
          )}
        </>
      }
    >
      {!data && !error && <Skeleton rows={4} tall />}
      {error && <LoadError text={error} onRetry={() => setAttempt((n) => n + 1)} />}
      {data && (moduleId === "star" ? <StarResult data={data} user={user} onPaywall={onPaywall} aiExpandEnabled={aiExpandEnabled} aiReportEnabled={aiReportEnabled} /> : <PlainResult moduleId={moduleId} data={data} onPaywall={onPaywall} aiExpandEnabled={aiExpandEnabled} aiReportEnabled={aiReportEnabled} />)}
    </Overlay>
  );
}

type SphereKey = "personality" | "spirit" | "realization" | "relations" | "health";

const STAR_TABS: { id: string; label: string; key?: SphereKey; section?: string }[] = [
  { id: "star", label: "Главное" },
  { id: "personality", label: "Личность", key: "personality" },
  { id: "spirit", label: "Дух и род", key: "spirit" },
  { id: "realization", label: "Реализация", key: "realization" },
  { id: "relations", label: "Отношения", key: "relations" },
  { id: "health", label: "Энергия", key: "health" },
  { id: "reco", label: "Рекомендации" },
  { id: "family", label: "Род", section: "family_lines" },
  { id: "mission", label: "Миссия", section: "mission" },
  { id: "lesson", label: "Урок", section: "main_lesson" },
];

const SECTION_TITLE: Record<string, string> = {
  Род: "Что досталось от рода",
  Миссия: "Миссия души",
  Урок: "Главный урок",
};

const SPHERE_TITLE: Record<SphereKey, string> = {
  personality: "Личность",
  spirit: "Дух и род",
  realization: "Реализация и деньги",
  relations: "Отношения",
  health: "Ресурс и энергия",
};

const SPHERE_SHORT: Record<SphereKey, string> = {
  personality: "Личность",
  spirit: "Дух и род",
  realization: "Реализация",
  relations: "Отношения",
  health: "Энергия",
};

/** Цвет-акцент для каждой сферы (см. .num-* в styles.css) — авторский выбор, не из внешней спеки. */
const SPHERE_COLOR: Record<SphereKey, string> = {
  personality: "num-gold",
  spirit: "num-purple",
  realization: "num-green",
  relations: "num-blue",
  health: "num-amber",
};

function StarResult({ data, user, onPaywall, aiExpandEnabled, aiReportEnabled }: { data: ReadingResponse; user: UserView; onPaywall: () => void; aiExpandEnabled: boolean; aiReportEnabled: boolean }) {
  const doc = data.reading.doc;
  const [tab, setTab] = useState("star");
  const current = STAR_TABS.find((t) => t.id === tab) ?? STAR_TABS[0]!;
  const byId = useMemo(() => new Map(doc.sections.map((s) => [s.id, s])), [doc.sections]);
  const lockedFor = (id: string) => doc.locked.some((l) => l.id === id);
  const name = firstName();

  const sphere: DocSection | undefined = current.key ? (byId.get(`sphere_${current.key}`) ?? byId.get("personality_teaser")) : undefined;
  const reco = byId.get("recommendations")?.parts?.find((p) => p.label === (current.key ? SPHERE_TITLE[current.key] : ""));
  const other = current.section ? byId.get(current.section) : undefined;
  const strengths = sphere?.parts?.find((p) => p.label === "Плюсовые проявления");
  const growth = sphere?.parts?.find((p) => p.label === "Зона внимания");

  const personalitySphere = byId.get("sphere_personality") ?? byId.get("personality_teaser");
  const personalityStrengths = personalitySphere?.parts?.find((p) => p.label === "Плюсовые проявления");
  const personalityGrowth = personalitySphere?.parts?.find((p) => p.label === "Зона внимания");
  const allReco = byId.get("recommendations");

  return (
    <>
      <div className="reporthead">
        <div className="reportkicker">Твой персональный разбор</div>
        <h1>{name ? `${name}, твой код` : "Твой код"}</h1>
        {user.birthDate && <p className="reportmeta">Дата рождения: {formatBirth(user.birthDate)}</p>}
      </div>

      <div className="tabs">
        {STAR_TABS.map((t) => (
          <button key={t.id} className={`tab${tab === t.id ? " on" : ""}`} onClick={() => setTab(t.id)}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="sheetbody">
        {tab === "star" && (
          <>
            <h2>Твои ключевые числа</h2>
            <div className="numrow">
              {(Object.keys(SPHERE_TITLE) as SphereKey[]).map((k) => (
                <button key={k} className="numtile" onClick={() => setTab(k)}>
                  <b className={SPHERE_COLOR[k]}>{doc.numbers[k]}</b>
                  <span>{SPHERE_SHORT[k]}</span>
                </button>
              ))}
            </div>

            {personalitySphere && (
              <div className="destcard">
                <h3>Твоя личность</h3>
                {(personalityStrengths || personalityGrowth) ? (
                  <div className="strengths">
                    {personalityStrengths && (
                      <div className="strengthcol">
                        <h4>Твои сильные стороны</h4>
                        <RichText text={personalityStrengths.text} />
                      </div>
                    )}
                    {personalityGrowth && (
                      <div className="strengthcol">
                        <h4>Твои зоны роста</h4>
                        <RichText text={personalityGrowth.text} />
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-block">{personalitySphere.body}</p>
                )}
                <button className="outline" onClick={() => setTab("personality")}>
                  Читать полный разбор →
                </button>
              </div>
            )}

            <div className="minicards">
              {(["realization", "relations", "health"] as SphereKey[]).map((k) => {
                const s = byId.get(`sphere_${k}`);
                const teaser = s?.parts?.find((p) => p.label === "Образ")?.text ?? firstBullet(s?.parts?.find((p) => p.label === "Плюсовые проявления")?.text ?? s?.body ?? "");
                return (
                  <button key={k} className="minicard" onClick={() => setTab(k)}>
                    <b className={SPHERE_COLOR[k]}>{SPHERE_TITLE[k]}</b>
                    {teaser && <p>{teaser}</p>}
                    <span className="arrow">Подробнее →</span>
                  </button>
                );
              })}
            </div>

            {allReco && (
              <button className="recobanner" onClick={() => setTab("reco")}>
                <span>
                  <b>Твои рекомендации</b>
                  <span>Персональные шаги по каждой сфере</span>
                </span>
                <span className="arrow">Смотреть →</span>
              </button>
            )}

            {doc.locked.length > 0 && <LockedList doc={doc} onPaywall={onPaywall} />}
            {aiExpandEnabled && <ExpandButton load={() => api.expandReading("star")} />}
            {aiReportEnabled && <AiReportButton load={() => api.aiReport("star")} pdfLoad={() => api.aiPdfLink("star").then(pdfOrThrow)} />}
          </>
        )}
        {tab === "reco" && (
          <>
            <h2>Твои рекомендации</h2>
            {allReco?.parts && allReco.parts.length > 0 ? (
              allReco.parts.map((p) => (
                <div className="item" key={p.label}>
                  <h4>{p.label}</h4>
                  <RichText text={p.text} />
                </div>
              ))
            ) : (
              <p className="text-block">{allReco?.body}</p>
            )}
            {doc.locked.length > 0 && <button className="primary" onClick={onPaywall}>Открыть полностью</button>}
          </>
        )}
        {current.key && sphere && (
          <>
            <SphereView sphereKey={current.key} section={sphere} />
            {byId.get(`sphere_${current.key}_full`) && (
              <SectionBlock section={byId.get(`sphere_${current.key}_full`)!} />
            )}
            {!byId.get(`sphere_${current.key}_full`) && lockedFor(`sphere_${current.key}_full`) && (
              <div className="locked">
                <span>🔒</span>
                <span>{SPHERE_TITLE[current.key]}: подробный разбор · <small>Полный</small></span>
              </div>
            )}
            {reco && (
              <div className="item">
                <h4>Что делать</h4>
                <RichText text={reco.text} />
              </div>
            )}
            {!reco && lockedFor("recommendations") && (
              <div className="locked">
                <span>🔒</span>
                <span>Рекомендации · <small>Полный</small></span>
              </div>
            )}
            {current.key === "health" && byId.get("health_attention") && (
              <div className="healthcard">
                <h3>{byId.get("health_attention")!.heading}</h3>
                {byId.get("health_attention")!.parts?.map((p) => (
                  <div key={p.label}>
                    <h4>{p.label}</h4>
                    <RichText text={p.text} />
                  </div>
                ))}
                <p className="healthnote">Это ориентиры для внимания к себе, а не диагноз и не медицинский совет. При любых жалобах обращайтесь к врачу.</p>
              </div>
            )}
            {current.key === "health" && !byId.get("health_attention") && lockedFor("health_attention") && (
              <div className="locked">
                <span>🔒</span>
                <span>На что обратить внимание в здоровье · <small>Полный</small></span>
              </div>
            )}
            {doc.locked.length > 0 && <button className="primary" onClick={onPaywall}>Открыть полностью</button>}
          </>
        )}
        {current.section && (
          <>
            <h2>{SECTION_TITLE[current.label] ?? current.label}</h2>
            {current.section === "family_lines" && other && ["lineMotherMale", "lineMotherFemale", "lineFatherMale", "lineFatherFemale", "fatalError"].every((k) => typeof doc.numbers[k] === "number") && (
              <KinDiagram
                mother={[doc.numbers.lineMotherMale as number, doc.numbers.lineMotherFemale as number]}
                father={[doc.numbers.lineFatherMale as number, doc.numbers.lineFatherFemale as number]}
                lesson={doc.numbers.fatalError as number}
              />
            )}
            {other ? <SectionBlock section={other} heading={null} /> : (
              <>
                <div className="locked">
                  <span>🔒</span>
                  <span>{SECTION_TITLE[current.label] ?? current.label} · <small>Полный</small></span>
                </div>
                <button className="primary" onClick={onPaywall}>Открыть полностью</button>
              </>
            )}
            {other && byId.get(`${current.section}_full`) && (
              <SectionBlock section={byId.get(`${current.section}_full`)!} />
            )}
            {other && !byId.get(`${current.section}_full`) && lockedFor(`${current.section}_full`) && (
              <div className="locked">
                <span>🔒</span>
                <span>{SECTION_TITLE[current.label] ?? current.label}: подробный разбор · <small>Полный</small></span>
              </div>
            )}
          </>
        )}
        <Notes doc={doc} />
      </div>
    </>
  );
}

/** Разбор одной сферы: число и образ, суть, сильные стороны и зона внимания, дополнительные блоки, практика. */
function SphereView({ sphereKey, section }: { sphereKey: SphereKey; section: DocSection }) {
  const parts = section.parts ?? [];
  const get = (label: string) => parts.find((p) => p.label === label)?.text;
  const m = /^(.*?)\s—\s(\d+):\s(.+)$/u.exec(section.heading ?? "");
  const image = get("Образ");
  const essence = get("Суть");
  const strengths = get("Плюсовые проявления");
  const growth = get("Зона внимания");
  const mantra = get("Мантра дня");
  const reflection = get("Вопрос для размышления");
  const rest = parts.filter(
    (p) => !["Образ", "Суть", "Плюсовые проявления", "Зона внимания", "Мантра дня", "Вопрос для размышления"].includes(p.label),
  );
  const practice = rest.find((p) => p.label.startsWith("Практика: "));
  const facts = rest.filter((p) => p !== practice);
  const structured = image !== undefined || essence !== undefined;

  if (!structured && !strengths && !growth) {
    return <SectionBlock section={section} heading={section.heading?.replace(/^.*?— (\d+): (.+)$/u, "Число №$1 · $2")} />;
  }
  return (
    <>
      <div className="spherehero">
        {m && <b className={`spherenum ${SPHERE_COLOR[sphereKey]}`}>{m[2]}</b>}
        <div>
          <h2>{SPHERE_TITLE[sphereKey]}</h2>
          {m && <div className="spherename">{m[3]}</div>}
        </div>
      </div>
      {image && <blockquote className="pullquote">{image}</blockquote>}
      {essence && <p className="text-block lead">{essence}</p>}
      {(strengths || growth) && (
        <div className="strengths">
          {strengths && (
            <div className="strengthcol">
              <h4>Твои сильные стороны</h4>
              <RichText text={strengths} />
            </div>
          )}
          {growth && (
            <div className="strengthcol">
              <h4>Зона внимания</h4>
              <RichText text={growth} />
            </div>
          )}
        </div>
      )}
      {mantra && (
        <div className="practicecard">
          <h4>Мантра дня</h4>
          <RichText text={mantra} />
        </div>
      )}
      {reflection && (
        <div className="factcard">
          <h4>Вопрос для размышления</h4>
          <RichText text={reflection} />
        </div>
      )}
      {facts.map((f) => (
        <div className="factcard" key={f.label}>
          <h4>{f.label}</h4>
          <RichText text={f.text} />
        </div>
      ))}
      {practice && (
        <div className="practicecard">
          <h4>{practice.label.replace(/^Практика:\s*/u, "Практика · ")}</h4>
          <RichText text={practice.text} />
        </div>
      )}
    </>
  );
}

const PAIR_ORDER = ["together", "meeting", "meeting_teaser", "conflict", "business", "lesson"];

function PlainResult({ moduleId, data, onPaywall, aiExpandEnabled, aiReportEnabled }: { moduleId: string; data: ReadingResponse; onPaywall: () => void; aiExpandEnabled: boolean; aiReportEnabled: boolean }) {
  const doc = data.reading.doc;
  const n = doc.numbers;
  let hero: ReactNode = null;

  if (moduleId === "compatibility") {
    hero = <StarDiagram numbers={{ personality: n.meeting ?? 0, spirit: n.month ?? 0, realization: n.year ?? 0, relations: n.lesson ?? 0, health: n.health ?? 0 }} active={null} labels={false} />;
  } else if (moduleId === "child_profile" && typeof n.energy === "number") {
    hero = <Trio top={n.energy} base={[]} />;
  } else if (moduleId === "success_code" && Array.isArray(n.code)) {
    hero = <div className="numline">{n.code.join(" · ")}</div>;
  } else if (moduleId === "money_archetypes" && Array.isArray(n.digits)) {
    hero = <div className="numline">{n.digits.join(" · ")}</div>;
  } else if (moduleId === "consciousness_mission") {
    hero = <div className="numline">{[n.consciousness, n.mission].join(" · ")}</div>;
  } else if (typeof n.archetype === "number") {
    hero = <div className="numline" style={{ fontSize: 60, letterSpacing: 0 }}>{n.archetype}</div>;
  }

  const sections = moduleId === "compatibility"
    ? [...doc.sections].sort((a, b) => rank(a.id) - rank(b.id))
    : doc.sections;

  return (
    <>
      {hero}
      <div className="result-head" style={{ margin: "6px 0 12px" }}>{doc.headline}</div>
      <div className="sheetbody">
        {moduleId === "compatibility" && <span className="chipnote">🔒 Дата второго человека не сохраняется</span>}
        {sections.map((s) => (
          <SectionBlock key={s.id} section={s} />
        ))}
        <LockedList doc={doc} onPaywall={onPaywall} />
        {aiExpandEnabled && !NEEDS_OTHER_DATE.has(moduleId) && <ExpandButton load={() => api.expandReading(moduleId)} />}
        {aiReportEnabled && !NEEDS_OTHER_DATE.has(moduleId) && <AiReportButton load={() => api.aiReport(moduleId)} pdfLoad={() => api.aiPdfLink(moduleId).then(pdfOrThrow)} />}
        <Notes doc={doc} />
      </div>
    </>
  );
}

/** Разборы с датой второго человека — у «полного разбора» нет этих данных, кнопку не показываем. */
const NEEDS_OTHER_DATE = new Set(["compatibility", "child_profile"]);

function rank(id: string): number {
  const i = PAIR_ORDER.indexOf(id);
  return i < 0 ? 99 : i;
}
